/**
 * Mobile-Optimized Audio Service
 * 
 * An enhanced audio system specifically designed for mobile applications:
 * - Handles platform-specific audio challenges (iOS, Android)
 * - Manages audio focus and interruptions
 * - Supports background music with proper lifecycle management
 * - Provides better battery efficiency
 * - Implements proper audio session categorization
 * - Supports offline audio playback with local storage
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { defaultSounds } from '@/components/game/soundlist';
// Import audio storage service for offline sound support
import { audioStorageService } from './audio-storage-service';

// Import BackgroundMode conditionally to avoid build errors if not installed
let BackgroundMode: any = null;
try {
  // Dynamic import to prevent build errors
  import('@awesome-cordova-plugins/background-mode').then(module => {
    BackgroundMode = module.BackgroundMode;
  }).catch(() => {
    console.warn('BackgroundMode plugin not available, background audio may not work properly');
  });
} catch (error) {
  console.warn('BackgroundMode import error:', error);
}

// Import the base audio cache for shared functionality
import { audioCache } from './audio-cache';

// Sound categories
export enum SoundCategory {
  UI = 'ui',
  Gameplay = 'gameplay',
  Win = 'win',
  Lose = 'lose',
  Background = 'background'
}

// Audio focus states
enum AudioFocus {
  Gain = 'gain',
  Loss = 'loss',
  LossTransient = 'loss_transient',
  LossTransientCanDuck = 'loss_transient_can_duck'
}

// Mobile audio service extends functionality beyond AudioCache
export class MobileAudioService {
  private isNative: boolean;
  private platform: string;
  private backgroundMusic: HTMLAudioElement | null = null;
  private backgroundMusicKey: string | null = null;
  private backgroundMusicVolume: number = 0.3; // Default background music volume
  private isBackgroundMusicMuted: boolean = true;
  private hasAudioFocus: boolean = true;
  private wasPlayingBeforeInterruption: boolean = false;
  private audioContext: AudioContext | null = null;
  private audioInitialized: boolean = false;
  
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.platform = Capacitor.getPlatform();
    
    // Setup event listeners for mobile-specific events
    if (this.isNative) {
      // Initialize audio context on first user interaction
      document.addEventListener('touchstart', () => this.initializeAudio(), { once: true });
      
      // Handle app state changes using App plugin instead of Plugins.App
      App.addListener('appStateChange', (state: any) => {
        if (state.isActive) {
          // App has come to foreground
          if (this.wasPlayingBeforeInterruption && this.backgroundMusic) {
            this.resumeBackgroundMusic();
          }
        } else {
          // App has gone to background
          this.wasPlayingBeforeInterruption = this.backgroundMusic?.paused === false;
          this.pauseBackgroundMusic();
        }
      });
      
      // Handle audio focus changes on Android
      if (this.platform === 'android' && window.cordova) {
        document.addEventListener('audiofocus', (event: any) => {
          this.handleAudioFocusChange(event.type as AudioFocus);
        });
      }
      
      // Handle audio interruptions on iOS
      if (this.platform === 'ios') {
        document.addEventListener('pause', () => {
          this.wasPlayingBeforeInterruption = this.backgroundMusic?.paused === false;
          this.pauseBackgroundMusic();
        });
        
        document.addEventListener('resume', () => {
          if (this.wasPlayingBeforeInterruption && this.backgroundMusic) {
            this.resumeBackgroundMusic();
          }
        });
      }
    }
    
    // Initialize audio context when available
    this.initializeAudio();
  }
  
  /**
   * Initialize the audio system
   */
  private initializeAudio(): void {
    if (this.audioInitialized) return;
    
    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContext) {
        this.audioContext = new AudioContext();
      }
      
      // Mark user interaction for web audio
      audioCache.markUserInteraction();
      
      this.audioInitialized = true;
    } catch (error) {
      console.warn('Failed to initialize audio context', error);
    }
    
    // Preload common UI sounds for immediate use
    this.preloadCommonSounds();
  }
  
  /**
   * Preloads frequently used sounds
   */
  private preloadCommonSounds(): void {
    const uiSounds = [
      'ui.button', 
      'ui.toggle', 
      'ui.notification',
      'ui.success',
      'ui.error'
    ];
    
    uiSounds.forEach(sound => {
      // Use the URLs from defaultSounds which are now pointing to DigitalOcean
      const url = defaultSounds[sound];
      if (url) {
        audioCache.preloadAudio(sound, url)
          .catch(err => console.warn(`Failed to preload UI sound ${sound}`, err));
      } else {
        console.warn(`Sound key ${sound} not found in defaultSounds`);
      }
    });
  }
  
  /**
   * Plays a sound with mobile optimizations
   * 
   * @param soundKey The key for the sound to play
   * @returns Promise that resolves when sound playback starts or fails
   */
  public async playSound(soundKey: string): Promise<void> {
    // For UI sounds, we want them to play even during audio focus loss
    const isUiSound = soundKey.startsWith('ui.');
    
    if (!this.hasAudioFocus && !isUiSound) {
      return Promise.resolve();
    }
    
    try {
      // Check if sound is available offline first
      if (this.isNative && await audioStorageService.isSoundDownloaded(soundKey)) {
        const localUrl = await audioStorageService.getLocalSoundUrl(soundKey);
        if (localUrl) {
          console.log(`🔊 Playing sound from local storage: ${soundKey}`);
          // Create a new Audio element for the local file
          const audio = new Audio(localUrl);
          audio.volume = audioCache.isMuted ? 0 : audioCache.getGlobalVolume();
          return audio.play().catch(error => {
            console.warn(`Failed to play local sound ${soundKey}, falling back to remote:`, error);
            // Fall back to remote sound if local playback fails
            return audioCache.play(soundKey);
          });
        }
      }
      
      // Fall back to remote sound via audioCache
      return audioCache.play(soundKey);
    } catch (error) {
      console.warn(`Error playing sound ${soundKey}:`, error);
      // Fall back to remote sound if any error occurs
      return audioCache.play(soundKey);
    }
  }
  
  /**
   * Starts playing background music
   * 
   * @param musicKey The key for the music to play
   * @param options Options for playback
   * @returns Promise that resolves when music playback starts
   */
  public async playBackgroundMusic(
    musicKey: string,
    options: { loop?: boolean; volume?: number } = {}
  ): Promise<void> {
    // Check if we have audio focus
    if (!this.hasAudioFocus) {
      this.backgroundMusicKey = musicKey; // Remember for later
      this.wasPlayingBeforeInterruption = true;
      return Promise.resolve();
    }
    
    // Stop any current background music
    this.stopBackgroundMusic();
    
    // Use external URL for background music via defaultSounds
    // or fall back to the original path structure
    const backgroundMusicKey = `background.${musicKey}`;
    let musicUrl = defaultSounds[backgroundMusicKey];
    
    // Check if background music is available offline first
    if (this.isNative && musicUrl) {
      try {
        const isDownloaded = await audioStorageService.isSoundDownloaded(backgroundMusicKey);
        if (isDownloaded) {
          const localUrl = await audioStorageService.getLocalSoundUrl(backgroundMusicKey);
          if (localUrl) {
            console.log(`🔊 Playing background music from local storage: ${backgroundMusicKey}`);
            musicUrl = localUrl;
          }
        } else {
          // Queue the background music for download for next time
          audioStorageService.queueSoundDownloadIfNeeded(backgroundMusicKey);
        }
      } catch (error) {
        console.warn(`Error checking offline background music ${backgroundMusicKey}:`, error);
      }
    }
    
    // If not found in defaultSounds, use the original path convention
    if (!musicUrl) {
      // Fall back to the original URL pattern using DigitalOcean base URL
      const SOUND_BASE_URL = 'https://teacherwilliam.sgp1.digitaloceanspaces.com';
      musicUrl = `${SOUND_BASE_URL}/sounds/background/${musicKey}.mp3`;
    }
    
    // Set up new background music
    const music = new Audio();
    
    music.src = musicUrl;
    music.loop = options.loop ?? false; // Default to false (no looping) unless explicitly set to true
    music.volume = this.isBackgroundMusicMuted ? 0 : (options.volume ?? this.backgroundMusicVolume);
    
    this.backgroundMusic = music;
    this.backgroundMusicKey = musicKey;
    
    // Apply platform-specific configuration to prevent system media UI
    if (this.isNative) {
      try {
        console.log('🔊 Configuring background music for ambient playback on mobile');
        
        // Set audio to not be the system's active audio session
        music.setAttribute('playsinline', 'true');  // iOS
        
        // Additional iOS attributes to prevent media UI
        if (this.platform === 'ios') {
          music.setAttribute('webkit-playsinline', 'true');
          music.setAttribute('x-webkit-airplay', 'deny');
        }
        
        // Set audio to be "ambient" or "silent" type
        // This helps prevent the audio from showing in system media controls
        if ('AudioContext' in window || 'webkitAudioContext' in window) {
          // If Web Audio API is available, we can route through it for more control
          const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
          const audioCtx = new AudioContextClass();
          
          // Connect to a silent destination node
          const source = audioCtx.createMediaElementSource(music);
          source.connect(audioCtx.destination);
        }
        
        // For Android, we set crossOrigin to prevent system media UI in some cases
        music.crossOrigin = 'anonymous';
      } catch (error) {
        console.warn('Failed to configure ambient audio playback:', error);
      }
    }
    
    return music.play()
      .catch(error => {
        console.warn(`Failed to play background music ${musicKey}`, error);
        this.backgroundMusic = null;
        this.backgroundMusicKey = null;
      });
  }
  
  /**
   * Pauses the current background music
   */
  public pauseBackgroundMusic(): void {
    if (this.backgroundMusic && !this.backgroundMusic.paused) {
      this.backgroundMusic.pause();
    }
  }
  
  /**
   * Resumes paused background music
   */
  public resumeBackgroundMusic(): Promise<void> {
    if (this.backgroundMusic && this.hasAudioFocus) {
      return this.backgroundMusic.play()
        .catch(error => {
          console.warn('Failed to resume background music', error);
        });
    }
    return Promise.resolve();
  }
  
  /**
   * Stops and clears the current background music
   */
  public stopBackgroundMusic(): void {
    if (this.backgroundMusic) {
      this.backgroundMusic.pause();
      this.backgroundMusic.src = '';
      this.backgroundMusic = null;
      this.backgroundMusicKey = null;
    }
    
    // Disable background mode if needed
    if (this.isNative && BackgroundMode) {
      try {
        if (BackgroundMode && typeof BackgroundMode.disable === 'function') {
          BackgroundMode.disable();
        }
      } catch (error) {
        // Ignore errors
      }
    }
  }
  
  /**
   * Sets the background music volume
   * 
   * @param volume Volume level from 0 to 1
   */
  public setBackgroundMusicVolume(volume: number): void {
    this.backgroundMusicVolume = Math.max(0, Math.min(1, volume));
    
    if (this.backgroundMusic && !this.isBackgroundMusicMuted) {
      this.backgroundMusic.volume = this.backgroundMusicVolume;
    }
  }
  
  /**
   * Mutes or unmutes background music
   * 
   * @param muted Whether the background music should be muted
   */
  public setBackgroundMusicMuted(muted: boolean): void {
    this.isBackgroundMusicMuted = muted;
    
    if (this.backgroundMusic) {
      this.backgroundMusic.volume = muted ? 0 : this.backgroundMusicVolume;
    }
  }
  
  /**
   * Sets the global sound effects volume (uses AudioCache)
   * 
   * @param volume Volume level from 0 to 1
   */
  public setSoundEffectsVolume(volume: number): void {
    audioCache.setGlobalVolume(volume);
  }
  
  /**
   * Mutes or unmutes all sound effects (uses AudioCache)
   * 
   * @param muted Whether sound effects should be muted
   */
  public setSoundEffectsMuted(muted: boolean): void {
    audioCache.setMuted(muted);
  }
  
  /**
   * Handles audio focus changes (primarily for Android)
   * 
   * @param focusState The new audio focus state
   */
  private handleAudioFocusChange(focusState: AudioFocus): void {
    switch (focusState) {
      case AudioFocus.Gain:
        // We gained audio focus
        this.hasAudioFocus = true;
        
        // Resume background music if it was playing before
        if (this.wasPlayingBeforeInterruption && this.backgroundMusic) {
          this.resumeBackgroundMusic();
        }
        break;
        
      case AudioFocus.Loss:
        // We lost audio focus indefinitely
        this.hasAudioFocus = false;
        this.wasPlayingBeforeInterruption = this.backgroundMusic?.paused === false;
        this.pauseBackgroundMusic();
        break;
        
      case AudioFocus.LossTransient:
        // We lost audio focus temporarily
        this.wasPlayingBeforeInterruption = this.backgroundMusic?.paused === false;
        this.pauseBackgroundMusic();
        break;
        
      case AudioFocus.LossTransientCanDuck:
        // We can keep playing but at a lower volume
        if (this.backgroundMusic) {
          const originalVolume = this.backgroundMusic.volume;
          this.backgroundMusic.volume = originalVolume * 0.2; // Reduce to 20% volume
          
          // Restore volume after 3 seconds if we haven't already handled a focus gain
          setTimeout(() => {
            if (this.backgroundMusic && this.hasAudioFocus) {
              this.backgroundMusic.volume = this.isBackgroundMusicMuted ? 0 : this.backgroundMusicVolume;
            }
          }, 3000);
        }
        break;
    }
  }
  
  /**
   * Preload a list of audio files
   * 
   * @param category The category of sounds to preload
   * @returns Promise that resolves when all sounds are preloaded
   */
  public async preloadSoundsForCategory(category: SoundCategory): Promise<void> {
    // Find all sounds that belong to this category
    const soundsInCategory = Object.entries(defaultSounds).filter(([key]) => 
      key.startsWith(`${category}.`)
    );
    
    if (soundsInCategory.length === 0) {
      console.warn(`No sounds found for category: ${category}`);
      return;
    }
    
    // Preload all files in the category
    const preloadPromises = soundsInCategory.map(([key, url]) => {
      return audioCache.preloadAudio(key, url)
        .catch(error => console.warn(`Failed to preload ${key} from ${url}`, error));
    });
    
    await Promise.all(preloadPromises);
  }
  
  /**
   * Clean up resources when app is closed
   */
  public dispose(): void {
    this.stopBackgroundMusic();
    
    if (this.audioContext) {
      this.audioContext.close().catch(console.error);
    }
    
    // Use the base audio cache to clean up remaining audio
    audioCache.reset(true);
  }
}

// Create a singleton instance for global use
export const mobileAudio = new MobileAudioService();

// Export default for easy imports
export default mobileAudio;